/*
 * $Id:: index.js 2021/03/02 11:11 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import { ReactComponent as RefreshIcon } from '../../assets/images/refresh_icon.svg';
import ErrorHandler from '../../common/components/ErrorHandler';
import { userCan } from '../../common/utils/service';
import ApplicationsTable from '../../components/ApplicationsTable';
import Card from '../../components/Card';
import CustomPagination from '../../components/CustomPagination';
import ApplicationFilters from '../../components/Filters/ApplicationFilters';
import PageTitle from '../../components/PageTitle';
import SmallPagination from '../../components/SmallPagination';
import WarningModal from '../../components/WarningModal';
import { t } from '../../providers';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from './constants';
import { list, loading as loadingSlice, error as errorSlice, exportLoading as exportLoadingSlice } from './draftApplicationsSlice';
import filterItems from './filterItems';
import { draftApplicationsParserList, filtersParser, myDraftsExportColumns } from './utils';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles((_theme) => ({
    filtersGrid: {
        margin: '30px 0px 40px 0',
    },
    tabs: {
        '& .MuiTabs-scroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'unset',
        },
        '& .MuiTab-root': {
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
            minWidth: '100px',
        },
    },
    tabContainer: {
        width: '100%',
    },
    cardTable: {
        marginRight: '0px',
        '@media (max-width:960px)': {
            marginRight: 'unset',
        },
    },
    refreshButton: {
        color: '#3F5E20',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textAlign: 'left',
        padding: '0px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const TRADEMARKS = 'trademark';
const DESIGNS = 'design';
const PATENTS = 'patents';
const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_SORT_COLUMN = 'creationDate';
// const DEFAULT_SORT_COLUMN = 'submissionDate';
const DEFAULT_SORT_TYPE = DESC;

const MyDraftsPage = () => {
    const classes = useStyles();
    const { service } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const loading = useSelector(loadingSlice);
    const exportLoading = useSelector(exportLoadingSlice);
    const error = useSelector(errorSlice);
    const applications = useSelector(list);
    const loggedInUser = useSelector(meSlice);
    const isTablet = useMediaQuery('(max-width:960px)');

    const { content, totalElements, size, number } = applications || {};

    const [tabs, setTabs] = useState([]);
    const [selectedTab, setSelectedTab] = useState(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0); //index of tabs
    const [sortProps, setSortProps] = useState({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
    const [draftApplicationsList, setDraftApplicationsList] = useState([]);
    const [filtersValues, setFiltersValues] = useState({ searchQuery: '', filters: {}, filtersChips: {} });
    const [requestBodyData, setRequestBodyData] = useState({
        paginationData: {
            page: DEFAULT_PAGE_NUMBER,
            size: DEFAULT_PAGE_SIZE,
        },
        sortingData: {
            sortColumn: sortProps.sortColumn,
            sortType: sortProps.sortType,
        },
        filteringData: filtersParser(filtersValues.filters),
        isDraft: true,
        isDashboard: false,
    });
    const [confirmDialog, setConfirmDialog] = useState({ visibility: false, message: '', params: { appId: '', ipRightType: '' } });
    const [errorDialog, setErrorDialog] = useState({ visibility: false, message: '', title: '' });
    const [errorHandler, setErrorHandler] = useState({ hasRefresh: false, message: '', subtitle: '' });
    const [filterDialog, setFilterDialog] = useState({ visibility: false, message: '' });
    const [hideInfo, setHideInfo] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [noDataToExport, setNoDataToExport] = useState(content && content.length === 0);
    const [tabChange, setTabChange] = useState(false);

    const toggleInfoMessage = (value) => {
        setHideInfo(value);
    };

    const handleCancelDeleteApplication = () => {
        setConfirmDialog({ visibility: false, message: '', params: { appId: '', ipRightType: '' } });
    };

    const handleCloseDialog = () => {
        setErrorDialog({ visibility: false, message: '', title: '' });
        dispatch({ type: `${SLICE_NAME}/error`, payload: false });
    };

    const handleCloseFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
        setFiltersValues({ ...filtersValues, filters: {}, filtersChips: {} });
    };

    const handleDateFilter = (idx) => {
        const updatedDateFilter = filtersValues.filters.dates.filter((filter, id) => id !== idx);
        const updatedDateChips = filtersValues.filtersChips.dates.filter((filter, id) => id !== idx);
        let updatedFilter = { ...filtersValues.filters, dates: updatedDateFilter };
        let updatedChips = { ...filtersValues.filtersChips, dates: updatedDateChips };
        if (!updatedDateFilter.length) {
            delete updatedFilter.dates;
        }
        if (!updatedDateChips.length) {
            delete updatedChips.dates;
        }
        setFiltersValues({
            searchQuery: filtersValues.searchQuery,
            filters: updatedFilter,
            filtersChips: updatedChips,
        });
    };

    const handleCancelFilterDialog = () => {
        setFilterDialog({ visibility: false, message: '' });
    };

    const deleteApplication = (appId, ipRightType) => {
        setConfirmDialog({ visibility: true, message: t('delete.application.failed'), params: { appId: appId, ipRightType: ipRightType } });
    };

    const resumeApplication = (appId, name) => {
        dispatch({ type: `${SLICE_NAME}/requestResumeDraftApplication`, payload: { name, appId, applicationType: tabs[selectedTab] } });
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        if (newPage !== number) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: newPage,
                    size: size,
                },
            });
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = event;
        if (size !== pageSize) {
            setRequestBodyData({
                ...requestBodyData,
                paginationData: {
                    page: 0,
                    size: pageSize,
                },
            });
        }
    };

    const handleTabChange = (event, value) => {
        event.preventDefault();
        setTabChange(true);
        setHideInfo(false);
        setFiltersValues({ searchQuery: '', filters: {} });
        setSortProps({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
        setRequestBodyData({
            ...requestBodyData,
            paginationData: {
                page: DEFAULT_PAGE_NUMBER,
                size: DEFAULT_PAGE_SIZE,
            },
            sortingData: {
                sortColumn: DEFAULT_SORT_COLUMN,
                sortType: DEFAULT_SORT_TYPE,
            },
            filteringData: {},
            isDraft: true,
            isDashboard: false,
        });
        history.push(`/my-drafts/${tabs[value]}`);
        // setSelectedTab(value);
    };

    const clearFilters = () => {
        clearFiltersWarning();
    };

    const clearFiltersWarning = () => {
        setFilterDialog({ visibility: true, message: 'nofilters.warning.text' });
    };

    const exportApplications = (type) => {
        setExportError(false);
        const exportedColumns = myDraftsExportColumns(service || tabs[selectedTab]);
        const params = {
            requestBody: {
                ...requestBodyData,
                columns: type === 'excel' ? exportedColumns : [],
                isDraft: true,
                isDashboard: false,
            },
            applicationType: service || tabs[selectedTab],
            type: type ? type : 'excel',
        };
        if (!params.requestBody.columns.length) delete params.requestBody.columns; // remove columns attribute if empty
        dispatch({ type: `${SLICE_NAME}/requestMyDraftsExport`, payload: { params } });
    };

    const _initiateServices = (_service) => {
        // Do nothing for now.
    };

    useEffect(() => {
        const { authorities } = loggedInUser;
        const allowedTabs = [];
        if (userCan(authorities, 'ROLE_TRADEMARKS')) allowedTabs.push(TRADEMARKS);
        if (userCan(authorities, 'ROLE_DESIGNS')) allowedTabs.push(DESIGNS);
        if (userCan(authorities, 'ROLE_PATENTS')) allowedTabs.push(PATENTS);

        if (allowedTabs.length > 0) {
            setTabs(allowedTabs); //set tabs depending on tabs allowed based on authorities role
        } else {
            history.push('/');
        }
    }, [loggedInUser, history]);

    const requestApplications = () => {
        setErrorHandler({ hasRefresh: false, message: '', subtitle: '' });
        setExportError(false);
        const params = {
            requestBody: requestBodyData,
            selectedTab: service || tabs[selectedTab],
        };
        dispatch({ type: `${SLICE_NAME}/requestDraftApplications`, payload: { params } });
        setTabChange(false);
    };

    useEffect(() => {
        if (tabs.length > 0 && !loading) {
            setSelectedTab(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0);
            requestApplications();
        }
    }, [dispatch, requestBodyData, tabs]);

    useEffect(() => {
        if (!tabChange) {
            if (tabs.length > 0 && !loading) {
                setSelectedTab(!!~tabs.indexOf(service) ? tabs.indexOf(service) : 0);
                setHideInfo(false);
                setTabChange(true);
                setFiltersValues({ searchQuery: '', filters: {} });
                setSortProps({ sortColumn: DEFAULT_SORT_COLUMN, sortType: DEFAULT_SORT_TYPE });
                setRequestBodyData({
                    ...requestBodyData,
                    paginationData: {
                        page: DEFAULT_PAGE_NUMBER,
                        size: DEFAULT_PAGE_SIZE,
                    },
                    sortingData: {
                        sortColumn: DEFAULT_SORT_COLUMN,
                        sortType: DEFAULT_SORT_TYPE,
                    },
                    filteringData: {},
                    isDraft: true,
                    isDashboard: false,
                });
                requestApplications();
            }
        }
    }, [service]);

    useEffect(() => {
        if (!tabChange) {
            if (filtersValues.searchQuery) {
                setRequestBodyData({
                    ...requestBodyData,
                    paginationData: { ...requestBodyData.paginationData, page: 0 },
                    filteringData: filtersParser(filtersValues.filters),
                    searchingData: filtersValues.searchQuery,
                });
            } else {
                const bodyData = { ...requestBodyData };
                delete bodyData['searchingData'];
                setRequestBodyData({
                    ...bodyData,
                    paginationData: { ...bodyData.paginationData, page: 0 },
                    filteringData: filtersParser(filtersValues.filters),
                });
            }
        }
    }, [filtersValues]);

    useEffect(() => {
        if (tabs.length > 0) {
            setNoDataToExport(content && content.length === 0);
            const draftAppsList = draftApplicationsParserList(applications || [], tabs[selectedTab], deleteApplication, resumeApplication);
            setDraftApplicationsList(draftAppsList);
        }
    }, [applications, selectedTab]);

    useEffect(() => {
        if ((error && error.description === 'Could not fetch application pdf') || error === 'Could not fetch application pdf') {
            setErrorDialog({ visibility: true, message: t('application.pdf.failed.fetching'), title: '' });
        } else if (
            (error && error.description === 'Could not fetch false applications excel') ||
            error === 'Could not fetch false applications excel'
        ) {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.excel.unavailable'), title: t('export.error.title') });
        } else if ((error && error.description === 'Could not fetch false applications pdf') || error === 'Could not fetch false applications pdf') {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.pdf.unavailable'), title: t('export.error.title') });
        } else if (error && error.description === 'error.draft.delete') {
            setErrorDialog({ visibility: true, message: t('error.draft.delete'), title: '' });
        } else if (error && error.description === 'error.draft.locked') {
            setErrorDialog({ visibility: true, message: t('error.draft.locked', { user: error.details.lockedBy }), title: '' });
        } else if (error && error.description === 'error.draft.resume') {
            setErrorDialog({ visibility: true, message: t('error.draft.resume'), title: '' });
        } else if (error && error.description === 'export.pdf.maxLength') {
            setExportError(true);
            setErrorDialog({ visibility: true, message: t('export.pdf.maxLength'), title: t('export.error.title') });
        } else {
            if (error && error !== null && error.extraInfo) {
                setNoDataToExport(true);
                setErrorHandler({
                    message: error.message || 'something.went.wrong',
                    hasRefresh: error.extraInfo.hasRefresh || false,
                    subtitle: error.extraInfo.secondMessage || '',
                });
            }
        }
    }, [error]);

    const setSorting = (sortColumn) => {
        const isSameKey = sortColumn === sortProps.sortColumn;
        const oppositeSort = sortProps.sortType === ASC ? DESC : ASC;
        const sortType = !isSameKey ? ASC : oppositeSort;
        setSortProps({ sortColumn, sortType });
        setRequestBodyData({
            ...requestBodyData,
            paginationData: {
                page: DEFAULT_PAGE_NUMBER,
                size: size,
            },
            sortingData: {
                sortColumn: sortColumn,
                sortType: sortType,
            },
        });
    };

    const handleAcceptDeletion = () => {
        const params = {
            requestBody: requestBodyData,
            selectedTab: tabs[selectedTab],
        };
        dispatch({
            type: `${SLICE_NAME}/requestDraftApplicationDelete`,
            payload: {
                appId: confirmDialog.params.appId,
                ipRightType: confirmDialog.params.ipRightType,
                applicationType: service || tabs[selectedTab],
                actionParams: {
                    type: `${SLICE_NAME}/requestDraftApplications`,
                    payload: { params },
                },
            },
        });
        setConfirmDialog({ visibility: false, message: '', appId: '' });
    };

    return (
        <div>
            {/* title */}
            <PageTitle title={t('my.draft.applications.page.title')} details={t('my.draft.application.description')} />

            {/* tabs */}
            <Tabs
                className={classes.tabs}
                value={selectedTab}
                onChange={handleTabChange}
                textColor="primary"
                aria-label={t('my.draft.applications.page.title')}>
                {tabs.map((tab, index) => (
                    <Tab
                        disableFocusRipple
                        id={`tab-${tab}-${index}`}
                        key={`tab-${index}`}
                        label={t(tab + '.tab.title')}
                        style={{ textTransform: 'uppercase' }}
                        aria-selected={selectedTab === index ? 'true' : 'false'}
                        role="tab"
                        aria-controls={`mydrafts-tabpanel-${index}`}
                    />
                ))}
            </Tabs>

            {/* list */}
            <div
                className={classes.cardTable}
                role="tabpanel"
                id={`mydrafts-tabpanel-${selectedTab}`}
                aria-labelledby={`tab-${tabs[selectedTab]}-${selectedTab}`}>
                <Card canEdit={false}>
                    {/* filters - search  */}
                    <ApplicationFilters
                        applicationType={tabs[selectedTab]}
                        filtersValues={filtersValues}
                        setFiltersValues={setFiltersValues}
                        clearFilters={clearFilters}
                        requestBodyData={requestBodyData}
                        exportApplications={exportApplications}
                        filterItems={filterItems}
                        datesOptions={'dateIntervalDraft'}
                        isTablet={isTablet}
                        toggleInfoMessage={toggleInfoMessage}
                        hideInfo={hideInfo}
                        handleDateFilter={handleDateFilter}
                        expoTypes={['excel']}
                        exportError={exportError}
                        exportLoading={exportLoading}
                        noDataToExport={noDataToExport}
                        page={'drafts'}
                        searchLabel={'search.in.draft.text'}
                    />
                    {errorHandler.message !== '' && (
                        <ErrorHandler
                            title={errorHandler.message}
                            subtitle={errorHandler.subtitle}
                            action={
                                errorHandler.hasRefresh && (
                                    <IconButton
                                        disableFocusRipple
                                        className={classes.refreshButton}
                                        onClick={() => {
                                            requestApplications();
                                        }}>
                                        {t('refresh.button.text')}
                                        <RefreshIcon style={{ paddingLeft: '6.68px' }} aria-hidden="true" focusable="false" />
                                    </IconButton>
                                )
                            }
                        />
                    )}

                    {!!content &&
                        !isTablet &&
                        draftApplicationsList &&
                        draftApplicationsList.data &&
                        !!draftApplicationsList.data.length &&
                        errorHandler.message === '' && (
                            <SmallPagination
                                defaultPageSize={DEFAULT_PAGE_SIZE}
                                totalElements={totalElements}
                                component={'div'}
                                count={totalElements || 0}
                                size={size || DEFAULT_PAGE_SIZE}
                                page={number || DEFAULT_PAGE_NUMBER}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                selectProps={{ renderValue: (value) => `${value} ${t('items')}` }}
                                paginationId={'myDrafts'}
                            />
                        )}
                    {/** list */}
                    {!loading ? (
                        errorHandler.message === '' && (
                            <ApplicationsTable
                                setSorting={setSorting}
                                sortBy={sortProps.sortColumn}
                                sortType={sortProps.sortType}
                                applications={draftApplicationsList}
                                tableId={'myDrafts'}
                                tableAriaLabel={t('my.draft.application.description')}
                            />
                        )
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress aria-hidden="true" />
                            <Typography variant="srOnly" role="alert">
                                {t('loading.text')}
                            </Typography>
                        </Box>
                    )}
                </Card>
            </div>
            {/* pagination */}
            {!!content &&
                errorHandler.message === '' &&
                draftApplicationsList &&
                draftApplicationsList.data &&
                !!draftApplicationsList.data.length && (
                    <CustomPagination
                        content={content}
                        defaultPageSize={DEFAULT_PAGE_SIZE}
                        totalElements={totalElements}
                        component={'div'}
                        count={totalElements || 0}
                        size={size || DEFAULT_PAGE_SIZE}
                        page={number || DEFAULT_PAGE_NUMBER}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        selectProps={{ renderValue: (value) => `${value} ${t('items')}` }}
                        paginationId={'myDrafts'}
                        isTablet={isTablet}
                    />
                )}
            {/* confirmation modal */}
            {confirmDialog.visibility && (
                <WarningModal
                    visibility={confirmDialog.visibility}
                    message={confirmDialog.message}
                    onConfirmation={handleAcceptDeletion}
                    confirmButtonText={t('delete.draft.application')}
                    cancelButtonText={t('cancel')}
                    onCancel={handleCancelDeleteApplication}
                />
            )}
            {errorDialog.visibility && (
                <WarningModal
                    visibility={errorDialog.visibility}
                    message={errorDialog.message}
                    onConfirmation={handleCloseDialog}
                    confirmButtonText={t('export.unavailable.button')}
                    title={errorDialog.title}
                />
            )}
            {filterDialog.visibility && (
                <WarningModal
                    visibility={filterDialog.visibility}
                    message={t(filterDialog.message)}
                    onConfirmation={handleCancelFilterDialog}
                    confirmButtonText={t('cancel')}
                    cancelButtonText={t('continue')}
                    onCancel={handleCloseFilterDialog}
                />
            )}
        </div>
    );
};

export default MyDraftsPage;
